import React, { useState, useEffect } from 'react';
import { Layout, Typography, Spin, Result, Table } from 'antd';
import {
  MoonOutlined,
  SunOutlined,
  LoadingOutlined,
  SwapOutlined,
  PlayCircleOutlined,
  ColumnWidthOutlined,
  MenuOutlined,
  DollarOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { Currency, MediaObject } from '../types';
import { useTheme } from '../contexts/ThemeContext';
import * as api from '../services/api';

const { Content } = Layout;
const { Title, Text } = Typography;

const DEFAULT_PAGE_TITLE = 'مكاتب الصرافة - Sila';

// Display-page theme (from user specs)
const DISPLAY_THEME = {
  dark: {
    pageBg: '#021323',
    cardBg: 'rgba(255, 255, 255, 0.1)',
    border: '#607FA6',
    text: '#ffffff',
    textSecondary: 'rgba(255,255,255,0.7)',
    searchBg: 'rgba(255,255,255,0.05)',
    searchBorder: '#607FA6',
    tableHeaderBg: " rgba(82, 108, 149,  0.1)",
    rowAltBg: " rgba(82, 108, 149,  0.1)",
    rowBg: "linear-gradient(336.37deg, rgba(2, 19, 35, 0.19) 11.26%, rgba(2, 19, 35, 0.19) 11.27%, rgba(2, 19, 35, 0.19) 22.07%, rgba(33, 135, 255, 0.19) 82.14%)",
    buyGradient: 'linear-gradient(135deg, rgba(34, 197, 94, 0.35) 0%, rgba(22, 163, 74, 0.4) 100%)',
    sellGradient: 'linear-gradient(135deg, rgba(239, 68, 68, 0.35) 0%, rgba(220, 38, 38, 0.4) 100%)',
    buyColor: '#FFFFFF',
    sellColor: '#FFFFFF',
  },
  light: {
    pageBg: '#EAEFF5',
    cardBg: '#FFFFFF',
    border: 'rgba(96, 127, 166, 0.4)',
    text: '#1e293b',
    textSecondary: '#64748b',
    searchBg: '#FFFFFF',
    searchBorder: 'rgba(96, 127, 166, 0.5)',
    tableHeaderBg: '#f1f5f9',
    rowBg: '#FFFFFF',
    rowAltBg: '#EAEFF5',
    buyGradient: 'linear-gradient(135deg, rgba(34, 197, 94, 0.12) 0%, rgba(34, 197, 94, 0.08) 100%)',
    sellGradient: 'linear-gradient(135deg, rgba(239, 68, 68, 0.12) 0%, rgba(239, 68, 68, 0.08) 100%)',
    buyColor: '#16a34a',
    sellColor: '#dc2626',
  },
};

const SYRIA_TZ = 'Asia/Damascus'; // Define the timezone for Syria



function formatSyriaDateTime(date: Date): { date: string; time: string; full: string } {
  // Create a new date object that adds 3 hours
  const adjustedDate = new Date(date.getTime() + 3 * 60 * 60 * 1000);

  const datePart = adjustedDate.toLocaleDateString('en-GB', {
    timeZone: SYRIA_TZ,
    day: '2-digit',
    month: '2-digit',
    year: '2-digit'
  });

  const timePart = adjustedDate.toLocaleTimeString('en-GB', {
    timeZone: SYRIA_TZ,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });

  return {
    date: datePart,
    time: timePart,
    full: `${datePart} ${timePart}` // Use template literals correctly
  };
}

const getMediaUrl = (media: MediaObject | string | null | undefined): string => {
  if (!media) return '';
  if (typeof media === 'string') return media;
  return media.url || '';
};

interface OfficeInfo {
  id: number;
  name: string;
  website?: string;
  logo?: string;
  video?: string;
  videoOption?: string;
  updatedAt?: string;
  /** مصطلحات العرض (من المكتب/قاعدة البيانات) */
  labelExchangeRates?: string;
  labelCurrencies?: string;
  labelWeBuyAt?: string;
  labelWeSellAt?: string;
}

function useOfficeIdentifier(): string | null {
  const { officeId } = useParams<{ officeId?: string }>();
  const { pathname } = useLocation();
  const subdomain = api.getSubdomainSlug();
  if (subdomain) return subdomain;
  if (officeId && pathname.startsWith('/display/')) return officeId;
  return null;
}

const LAYOUT_BREAKPOINT = 992;

const Display: React.FC = () => {
  const officeIdentifier = useOfficeIdentifier();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const t = isDark ? DISPLAY_THEME.dark : DISPLAY_THEME.light;

  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const [windowHeight, setWindowHeight] = useState(typeof window !== 'undefined' ? window.innerHeight : 768);
  const themeSyncedFromUrl = React.useRef(false);

  useEffect(() => {
    const onResize = () => {
      setWindowWidth(window.innerWidth);
      setWindowHeight(window.innerHeight);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ?m=1 عرضي غامق | ?m=2 عرضي فاتح | ?m=3 طولي غامق | ?m=4 طولي فاتح
  const modeParam = searchParams.get('m');
  const modeFromUrl: 1 | 2 | 3 | 4 | null =
    modeParam === '1' || modeParam === '2' || modeParam === '3' || modeParam === '4'
      ? (Number(modeParam) as 1 | 2 | 3 | 4)
      : null;
  const themeFromMode =
    modeFromUrl === 1 || modeFromUrl === 3
      ? 'dark'
      : modeFromUrl === 2 || modeFromUrl === 4
        ? 'light'
        : null;
  const layoutFromMode =
    modeFromUrl === 1 || modeFromUrl === 2
      ? 'h'
      : modeFromUrl === 3 || modeFromUrl === 4
        ? 'v'
        : null;

  // مزامنة النمط من الرابط ?m=... أو ?theme=dark|light عند التحميل فقط
  useEffect(() => {
    if (themeSyncedFromUrl.current) return;
    themeSyncedFromUrl.current = true;
    const themeParam = themeFromMode || searchParams.get('theme');
    if (themeParam === 'dark' && !isDark) toggleTheme();
    else if (themeParam === 'light' && isDark) toggleTheme();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggleTheme = () => {
    toggleTheme();
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      const nextTheme = isDark ? 'light' : 'dark';
      next.set('theme', nextTheme);
      // Keep ?m= in sync when present
      if (next.has('m')) {
        const currentMode = next.get('m');
        const isHorizontalMode = currentMode === '1' || currentMode === '2';
        next.set('m', isHorizontalMode ? (nextTheme === 'dark' ? '1' : '2') : (nextTheme === 'dark' ? '3' : '4'));
      }
      return next;
    });
  };

  const horizontalParam =
    layoutFromMode === 'h' ||
    searchParams.get('h') === '1' ||
    searchParams.get('h') === 'true' ||
    searchParams.get('horizontal') === 'true' ||
    searchParams.get('layout') === 'horizontal';
  const verticalParam =
    layoutFromMode === 'v' ||
    searchParams.get('v') === '1' ||
    searchParams.get('v') === 'true' ||
    searchParams.get('vertical') === 'true';
  const effectiveIsHorizontal = verticalParam ? false : horizontalParam ? true : windowWidth >= LAYOUT_BREAKPOINT;
  const isVertical = !effectiveIsHorizontal;
  const isHorizontal = effectiveIsHorizontal;

  const toggleLayout = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (effectiveIsHorizontal) {
        next.set('v', '1');
        next.delete('h');
        next.delete('horizontal');
        next.delete('layout');
        if (next.has('m')) {
          next.set('m', isDark ? '3' : '4');
        }
      } else {
        next.set('h', '1');
        next.delete('v');
        next.delete('vertical');
        if (next.has('m')) {
          next.set('m', isDark ? '1' : '2');
        }
      }
      return next;
    });
  };

  const [office, setOffice] = useState<OfficeInfo | null>(null);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeVideo, setActiveVideo] = useState<'main' | 'option'>('main');
  const initialLoadDone = React.useRef(false);

  const videos = { main: office?.video || '', option: office?.videoOption || '' };
  const hasMultipleVideos = !!(videos.main && videos.option);
  const currentVideoUrl = activeVideo === 'main' ? videos.main : videos.option;
  const hasAnyVideo = !!(videos.main || videos.option);

  const REFRESH_INTERVAL_MS = 10 * 60 * 1000;

  useEffect(() => {
    let isFirstRun = true;

    const fetchData = async () => {
      if (!officeIdentifier?.trim()) {
        navigate('/404');
        return;
      }
      if (isFirstRun) setLoading(true);
      setError(null);
      const fetchBySlug = () => api.getPublicCurrenciesBySlug(officeIdentifier);
      const fetchById = () => api.getPublicCurrencies(parseInt(officeIdentifier, 10));
      const isNumeric = /^\d+$/.test(officeIdentifier.trim());
      try {
        const data: { data: Currency[], lastUpdatedAt: string } = isNumeric ? (await fetchById()) : (await fetchBySlug());
        console.log(data);
        if (data?.data?.length && data.data.length > 0) {
          const first = data.data[0];
          if (first.exchangeOffice) {
            const od = first.exchangeOffice;
            if (od.status !== 'active') {
              navigate('/404');
              return;
            }
            if (!initialLoadDone.current) {
              setOffice({
                id: od.id,
                name: od.name,
                website: od.website || undefined,
                logo: getMediaUrl(od.logo),
                video: getMediaUrl(od.video),
                videoOption: getMediaUrl(od.videoOption),
              });
              initialLoadDone.current = true;
            }
          } else {
            navigate('/404');
            return;
          }
          setCurrencies([...data.data].sort((a, b) => a.order - b.order));
          setLastUpdate(data?.lastUpdatedAt ? new Date(data.lastUpdatedAt) : new Date());
        } else {
          navigate('/404');
          return;
        }
      } catch (err: any) {
        if (
          err.message?.includes('404') ||
          /not found|غير موجود|Office not found/i.test(err.message || '')
        ) {
          navigate('/404');
          return;
        }
        setError(err.message || 'فشل في تحميل البيانات');
      } finally {
        if (isFirstRun) setLoading(false);
      }
      isFirstRun = false;
    };

    fetchData();
    const refreshInterval = setInterval(fetchData, REFRESH_INTERVAL_MS);
    return () => clearInterval(refreshInterval);
  }, [officeIdentifier, navigate]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const sub = api.getSubdomainSlug();
    if (!sub || !office) return;
    const prevTitle = document.title;
    document.title = office.name;
    let link = document.querySelector<HTMLLinkElement>("link[rel*='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    const prevHref = link.href;
    if (office.logo) link.href = office.logo;
    return () => {
      document.title = prevTitle || DEFAULT_PAGE_TITLE;
      if (link) link.href = prevHref || `${process.env.PUBLIC_URL || ''}/favicon.ico`;
    };
  }, [office]);

  const supportsFlagEmoji = () => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return false;

    ctx.fillText("🇸🇦", 0, 0);
    const data = ctx.getImageData(0, 0, 1, 1).data;

    // إذا الصورة كلها شفافة → المتصفح لا يدعم الأعلام
    return !(data[0] === 0 && data[1] === 0 && data[2] === 0 && data[3] === 0);
  };

  const getFlagIcon = (code?: string) => {
    if (!code) return "";

    const country = code.trim().toLowerCase();

    // 1) إذا المتصفح يدعم الإيموجي → نرجع الإيموجي
    if (supportsFlagEmoji()) {
      return country
        .toUpperCase()
        .replace(/./g, char =>
          String.fromCodePoint(char.charCodeAt(0) + 127397)
        );
    }

    // 2) إذا لا يدعم → نرجع رابط SVG
    return `https://flagcdn.com/48x36/${country}.png`;
  };

  const labelCurrencies = office?.labelCurrencies ?? 'Currencies';
  const labelCurrenciesAr = 'العملات';
  const labelWeBuyAt = office?.labelWeBuyAt ?? 'WE BUY AT';
  const labelWeSellAt = office?.labelWeSellAt ?? 'WE SELL AT';
  /** لون فاتح ثابت لنص "سعر الشراء" و "سعر البيع" تحت العناوين */
  const labelSubColor = isDark ? 'rgba(255,255,255,0.88)' : 'rgba(100,116,139,0.95)';
  // 7 صفوف على الأقل تملأ الشاشة، العلم يملأ ارتفاع الصف (بالنمطين)
  // const ROWS_VISIBLE = 7;
  // const verticalReserved = 320; // هيدر + فيديو + فوتر تقريبي للطولي
  // const horizontalReserved = 180; // هيدر + فوتر للعرضي
  const rowHeight = 60;
  const currencyCellMinHeight = rowHeight;
  const flagH = rowHeight - 1;
  const flagW = Math.round(flagH * (4 / 3));
  const flagEmojiSize = flagH;
  const rateFontSize = 'clamp(40px, 4.2vw, 80px)';
  const rateMinHeight = rowHeight;
  const currencyNameSize = 'clamp(14px, 2vw, 26px)';
  const currencyNameArSize = 'clamp(11px, 1.4vw, 18px)';

  const columns = [
    {
      title: (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', minHeight: 56, textAlign: 'center', whiteSpace: 'nowrap', fontSize: 'clamp(18px, 2.4vw, 32px)', fontWeight: 700 }}>
          <span>{labelWeBuyAt}</span>
          <span style={{ fontWeight: 500, color: labelSubColor, fontSize: '0.85em' }}> سعر الشراء</span>
        </div>
      ),

      dataIndex: 'buy',
      key: 'buy',
      width: 520,
      align: 'center' as const,
      render: (buy: number | string, _record: Currency, rowIndex: number) => (
        <div
          className="display-rate-cell display-rate-buy"
          style={{
            color: '#e53935',
            textShadow: '0 0 1px rgba(0,0,0,0.3)',
            padding: '0px',
            fontWeight: 700,
            fontSize: rateFontSize,
            lineHeight: 1.1,
            textAlign: 'center',
            background: 'transparent',
            border: 'none',
            animationDelay: `${rowIndex * 0.2}s`,
            width: '100%',
            minHeight: rateMinHeight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {Number(buy).toLocaleString('en-US')}
        </div>
      ),
    },
    {
      title: (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', minHeight: 56, textAlign: 'center', whiteSpace: 'nowrap', fontSize: 'clamp(18px, 2.4vw, 32px)', fontWeight: 700 }}>
          <span>{labelWeSellAt}</span>
          <span style={{ fontWeight: 500, color: labelSubColor, fontSize: '0.85em' }}> سعر البيع</span>
        </div>
      ),
      dataIndex: 'sell',
      key: 'sell',
      width: 520,
      align: 'center' as const,
      render: (sell: number | string, _record: Currency, rowIndex: number) => (
        <div
          className="display-rate-cell display-rate-sell"
          style={{
            color: '#00c853',
            textShadow: '0 0 1px rgba(0,0,0,0.3)',
            padding: '0px',
            fontWeight: 700,
            fontSize: rateFontSize,
            lineHeight: 1.1,
            textAlign: 'center',
            background: 'transparent',
            border: 'none',
            animationDelay: `${rowIndex * 0.2}s`,
            width: '100%',
            minHeight: rateMinHeight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {Number(sell).toLocaleString('en-US')}
        </div>
      ),
    },
    {
      title: (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', width: '100%', minHeight: 56, gap: 2, textAlign: 'right', fontSize: 'clamp(18px, 2.4vw, 32px)', fontWeight: 700 }}>
          <span>{labelCurrencies}</span>
          <span style={{ fontSize: '0.85em', fontWeight: 500, color: t.textSecondary }}>{labelCurrenciesAr}</span>
        </div>
      ),
      key: 'currency',
      dataIndex: 'iso',
      width: 280,
      align: 'right' as const,
      render: (_: unknown, r: Currency) => {
        const flag = getFlagIcon(r.flag);
        const isEmoji = !flag.startsWith("http");
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              direction: 'rtl',
              justifyContent: 'flex-end',
              whiteSpace: 'nowrap',
              width: '100%',
              minHeight: currencyCellMinHeight,
              padding: '4px 0',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, minWidth: 0, flex: 1, justifyContent: 'center' }}>
              <span
                style={{
                  fontWeight: 700,
                  fontSize: currencyNameSize,
                  lineHeight: 1.15,
                  color: t.text,
                  direction: 'rtl',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {r.nameEn || r.iso}
              </span>
              <span style={{ fontSize: currencyNameArSize, color: labelSubColor, direction: 'rtl' }}>{r.nameAr}</span>
            </div>
            {isEmoji ? (
              <span className="display-flag-icon" style={{ fontSize: flagEmojiSize, flexShrink: 0, display: 'inline-block' }}>{flag}</span>
            ) : (
              <img className="display-flag-icon" src={flag} alt={r.flag} style={{ width: flagW, height: flagH, borderRadius: 4, flexShrink: 0, display: 'inline-block' }} />
            )}
          </div>
        );
      }
    },
  ];

  if (loading) {
    return (
      <Layout dir="rtl" style={{ minHeight: '100vh', background: t.pageBg }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
          }}
        >
          <Spin indicator={<LoadingOutlined style={{ fontSize: 56, color: t.border }} spin />} />
          <Text style={{ color: t.text, marginTop: 24, fontSize: 18 }}>جاري تحميل البيانات...</Text>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout style={{ minHeight: '100vh', background: t.pageBg }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
          }}
        >
          <Result
            status="warning"
            title={<span style={{ color: t.text, fontSize: 20 }}>{error}</span>}
            style={{
              background: isDark ? 'rgba(96, 127, 166, 0.1)' : t.cardBg,
              borderRadius: 16,
              padding: 40,
              border: `1px solid ${t.border}`,
            }}
          />
        </div>
      </Layout>
    );
  }

  const titleExchangeRates = office?.labelExchangeRates ?? 'Exchange Rates | أسعار الصرف';

  if (isHorizontal) {
    return (
      <Layout dir="ltr" style={{ minHeight: '100vh', background: t.pageBg, margin: 0, padding: 0 }}>
        <Content style={{ padding: 4, margin: 0, width: '100%', minHeight: '100vh', display: 'flex' }}>
          <div style={{ display: 'flex', width: '100%', minHeight: '100vh', gap: 8, alignItems: 'stretch' }}>
            {/* ثلث الشاشة: أعلى لوغو + اسم المكتب، وسط الفيديو، أسفل التواريخ */}
            <div
              style={{
                direction: 'ltr',
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minWidth: 0,
                background: t.cardBg,
                border: `1px solid ${t.border}`,
                borderRadius: 8,
                overflow: 'hidden',
              }}
            >
              {/* أعلى العرضي: اسم المكتب كبير يسار | التاريخ والوقت يمين (بدون لوغو) */}
              <div style={{ padding: '10px 14px', borderBottom: `1px solid ${t.border}`, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 36, fontWeight: 800, color: t.text }}>{office?.name || '—'}</span>
                <div style={{ fontSize: 14, color: t.textSecondary, fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                  {formatSyriaDateTime(currentTime).date} {formatSyriaDateTime(currentTime).time}
                  {lastUpdate && (
                    <span style={{ marginLeft: 8, color: '#22c55e', fontSize: 12 }}>• آخر تحديث: {formatSyriaDateTime(lastUpdate).date} {formatSyriaDateTime(lastUpdate).time}</span>
                  )}
                </div>
              </div>

              {/* لوغو في الأسفل فوق الريل */}
              <div style={{ padding: '10px 14px', borderBottom: `1px solid ${t.border}`, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                {office?.logo ? (
                  <img src={office.logo} alt="Logo" style={{ width: 192, height: 192, objectFit: 'contain', borderRadius: 12 }} />
                ) : (
                  <div style={{ width: 96, height: 96, background: isDark ? 'rgba(96, 127, 166, 0.2)' : '#f1f5f9', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', color: t.textSecondary, fontSize: 12 }}>Logo</div>
                )}
              </div>

              {/* ريل الصرافة — خلفية متحركة، أنيميشن كلمات، أيقونات عملات */}
              <div className="display-reel-container" style={{ flexShrink: 0, borderBottom: `1px solid ${t.border}`, margin: 10, borderRadius: 6 }}>
                <div className="display-reel-track">
                  <div className="display-reel-item display-reel-item-1 display-reel-bg-animated">
                    <DollarOutlined className="display-reel-icon" style={{ color: t.text }} />
                    <span className="display-reel-text display-reel-text-animate" style={{ color: t.text }}>أسعار الصرافة مباشرة</span>
                  </div>
                  <div className="display-reel-item display-reel-item-2 display-reel-bg-animated">
                    <SwapOutlined className="display-reel-icon" style={{ color: t.text }} />
                    <span className="display-reel-text display-reel-text-animate" style={{ color: t.text }}>شراء وبيع العملات</span>
                  </div>
                  <div className="display-reel-item display-reel-item-3 display-reel-bg-animated">
                    <GlobalOutlined className="display-reel-icon" style={{ color: t.text }} />
                    <span className="display-reel-text display-reel-text-animate" style={{ color: t.text }}>جميع العملات → ل.س</span>
                  </div>
                </div>
              </div>

              {/* وسط: الفيديو — عند وجود video + videoOption: الأول ينتهي يظهر الثاني، ينتهي يعود الأول */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 0 }}>
                {hasAnyVideo ? (
                  <>
                    {currentVideoUrl && !currentVideoUrl.includes('youtube.com') && !currentVideoUrl.includes('vimeo.com') ? (
                      <video
                        key={currentVideoUrl}
                        src={currentVideoUrl}
                        autoPlay
                        loop={!hasMultipleVideos}
                        muted
                        playsInline
                        controls
                        style={{ width: '100%', objectFit: 'contain' }}
                        onEnded={hasMultipleVideos ? () => setActiveVideo(activeVideo === 'main' ? 'option' : 'main') : undefined}
                      />
                    ) : currentVideoUrl ? (
                      <iframe src={currentVideoUrl} title="Video" style={{ width: '100%', height: '100%', border: 0 }} allow="autoplay; encrypted-media" allowFullScreen />
                    ) : null}
                    {hasMultipleVideos && (
                      <div style={{ padding: 4, borderTop: `1px solid ${t.border}`, flexShrink: 0 }}>
                        <button onClick={() => setActiveVideo(activeVideo === 'main' ? 'option' : 'main')} style={{ width: '100%', padding: '4px 8px', background: 'transparent', border: `1px solid ${t.border}`, borderRadius: 6, cursor: 'pointer', fontSize: 10, color: t.text, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                          <SwapOutlined /> {activeVideo === 'main' ? 'الفيديو الثاني' : 'الفيديو الأول'}
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: isDark ? 'rgba(96, 127, 166, 0.06)' : '#f8fafc', padding: 8 }}>
                    <PlayCircleOutlined style={{ fontSize: 32, color: t.textSecondary }} />
                    <span style={{ fontSize: 11, color: t.textSecondary, marginTop: 6 }}>لا يوجد فيديو</span>
                  </div>
                )}
              </div>

              {/* أسفل: أزرار التخطيط والثيم فقط */}
              <div style={{ padding: 8, borderTop: `1px solid ${t.border}`, flexShrink: 0 }}>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button onClick={toggleLayout} title={effectiveIsHorizontal ? 'طولي' : 'عرضي'} style={{ flex: 1, minWidth: 70, background: isDark ? 'rgba(96, 127, 166, 0.2)' : '#f1f5f9', border: `1px solid ${t.border}`, borderRadius: 8, padding: 8, cursor: 'pointer', color: t.text, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                    {effectiveIsHorizontal ? <MenuOutlined /> : <ColumnWidthOutlined />}
                    <span style={{ fontSize: 11 }}>{effectiveIsHorizontal ? 'طولي' : 'عرضي'}</span>
                  </button>
                  <button onClick={handleToggleTheme} style={{ background: isDark ? 'rgba(96, 127, 166, 0.2)' : '#f1f5f9', border: `1px solid ${t.border}`, borderRadius: 8, padding: 8, cursor: 'pointer', color: t.text }}>
                    {isDark ? <SunOutlined /> : <MoonOutlined />}
                  </button>
                </div>
              </div>
            </div>

            {/* ثلثا الشاشة: الجدول */}
            <div style={{ flex: 2, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <div style={{ padding: '10px 16px', background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 8, marginBottom: 8 }}>
                <Title level={4} style={{ margin: 0, color: t.text, textAlign: 'center' }}>{titleExchangeRates}</Title>
              </div>
              <div style={{ flex: 1, border: `1px solid ${t.border}`, borderRadius: 8, overflow: 'auto', background: t.cardBg }}>
                <Table
                  style={{ direction: 'rtl' }}
                  dataSource={currencies}
                  columns={columns}
                  rowKey="id"
                  pagination={false}
                  showHeader
                  rowClassName={(_, i) => (i % 2 === 1 ? 'display-row-alt' : 'display-row')}
                  locale={{ emptyText: 'لا توجد عملات متاحة' }}
                />
              </div>
              <div style={{ padding: '8px 16px', background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 8, marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <span style={{ fontSize: 14, color: '#22c55e', fontWeight: 600 }}>Live</span>
                <span style={{ fontSize: 14, color: t.textSecondary }}>LAST UPDATE AT {lastUpdate ? formatSyriaDateTime(lastUpdate).time : '--'}</span>
                <span style={{ fontSize: 14, color: t.textSecondary }}>All Rates → SYP</span>
              </div>
            </div>
          </div>
        </Content>
        <style>{`
          .ant-table { background: transparent !important; }
          .ant-table-thead > tr > th { background: ${t.tableHeaderBg} !important; color: ${t.text} !important; font-weight: 700 !important; font-size: clamp(18px, 2.4vw, 32px) !important; border-bottom: 1px solid ${t.border} !important; padding: 16px 20px !important; min-height: 64px !important; vertical-align: middle !important; }
          .ant-table-thead > tr > th:nth-child(1) { color: #e53935 !important; font-weight: 800 !important; }
          .ant-table-thead > tr > th:nth-child(2) { color: #00c853 !important; font-weight: 800 !important; }
          .display-row > td { background: ${t.rowBg} !important; border-bottom: 1px solid ${t.border} !important; padding: 4px !important; }
          .display-row-alt > td { background: ${t.rowAltBg} !important; border-bottom: 1px solid ${t.border} !important; padding: 8px 12px !important; }
          .ant-table-tbody > tr:hover > td { background: ${isDark ? 'rgba(96, 127, 166, 0.1)' : 'rgba(96, 127, 166, 0.06)'} !important; }
          @keyframes displayWaveBlinkBuy {
            0%, 100% { transform: translateY(0) scale(1); opacity: 1; filter: brightness(1); }
            50% { transform: translateY(-10px) scale(1.03); opacity: 0.85; filter: brightness(1.15); }
          }
          @keyframes displayWaveBlinkSell {
            0%, 100% { transform: translateY(0) scale(1); opacity: 1; filter: brightness(1); }
            50% { transform: translateY(-10px) scale(1.03); opacity: 0.85; filter: brightness(1.15); }
          }
          .display-rate-cell.display-rate-buy { animation: displayWaveBlinkBuy 2.2s ease-in-out infinite; }
          .display-rate-cell.display-rate-sell { animation: displayWaveBlinkSell 2.2s ease-in-out infinite; }
          @keyframes displayFlagPulse {
            0%, 100% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.18); opacity: 0.92; }
          }
          .display-flag-icon { display: inline-block; }
          .display-reel-container { height: 76px; overflow: hidden; border-radius: 0; position: relative; }
          .display-reel-track { display: flex; flex-direction: column; animation: displayReelScroll 10s ease-in-out infinite; }
          .display-reel-item { min-height: 76px; display: flex; align-items: center; justify-content: center; gap: 16px; padding: 0 24px; border-radius: 0; text-align: center; white-space: nowrap; position: relative; overflow: hidden; }
          .display-reel-item-1 { background: linear-gradient(135deg, rgba(14, 165, 233, 0.35) 0%, rgba(56, 189, 248, 0.2) 40%, rgba(2, 132, 199, 0.28) 100%); background-size: 200% 200%; animation: displayReelBgMove1 6s ease-in-out infinite; border-bottom: 1px solid rgba(255,255,255,0.1); }
          .display-reel-item-2 { background: linear-gradient(135deg, rgba(16, 185, 129, 0.32) 0%, rgba(52, 211, 153, 0.2) 40%, rgba(5, 150, 105, 0.26) 100%); background-size: 200% 200%; animation: displayReelBgMove2 6s ease-in-out infinite; border-bottom: 1px solid rgba(255,255,255,0.1); }
          .display-reel-item-3 { background: linear-gradient(135deg, rgba(251, 146, 60, 0.3) 0%, rgba(253, 186, 116, 0.18) 40%, rgba(234, 88, 12, 0.24) 100%); background-size: 200% 200%; animation: displayReelBgMove3 6s ease-in-out infinite; }
          .display-reel-bg-animated::before {
            content: ''; position: absolute; inset: 0; z-index: 0;
            background: linear-gradient(110deg, transparent 0%, rgba(255,255,255,0.1) 25%, transparent 50%, rgba(255,255,255,0.06) 75%, transparent 100%);
            background-size: 200% 100%; animation: displayReelShine 4s ease-in-out infinite;
          }
          @keyframes displayReelShine {
            0%, 100% { background-position: 200% 0; }
            50% { background-position: -100% 0; }
          }
          @keyframes displayReelBgMove1 {
            0%, 100% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
          }
          @keyframes displayReelBgMove2 {
            0%, 100% { background-position: 100% 50%; }
            50% { background-position: 0% 50%; }
          }
          @keyframes displayReelBgMove3 {
            0%, 100% { background-position: 50% 0%; }
            50% { background-position: 50% 100%; }
          }
          .display-reel-icon { font-size: 30px !important; flex-shrink: 0; z-index: 1; animation: displayReelIconPulse 2s ease-in-out infinite; }
          .display-reel-item-2 .display-reel-icon { animation-delay: 0.3s; }
          .display-reel-item-3 .display-reel-icon { animation-delay: 0.6s; }
          @keyframes displayReelIconPulse {
            0%, 100% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.1); opacity: 0.9; }
          }
          .display-reel-text { font-size: 21px; font-weight: 700; text-shadow: 0 1px 3px rgba(0,0,0,0.25); letter-spacing: 0.03em; z-index: 1; }
          .display-reel-text-animate { animation: displayReelTextIn 2.5s ease-in-out infinite; }
          .display-reel-item-2 .display-reel-text-animate { animation-delay: 0.2s; }
          .display-reel-item-3 .display-reel-text-animate { animation-delay: 0.4s; }
          @keyframes displayReelTextIn {
            0%, 100% { transform: translateX(0); opacity: 1; }
            25% { transform: translateX(4px); opacity: 0.95; }
            75% { transform: translateX(-4px); opacity: 0.95; }
          }
          @keyframes displayReelScroll {
            0%, 22% { transform: translateY(0); }
            28%, 50% { transform: translateY(-33.33%); }
            55%, 78% { transform: translateY(-66.66%); }
            83%, 100% { transform: translateY(0); }
          }
        `}</style>
      </Layout>
    );
  }

  return (
    <Layout dir="rtl" className="display-layout-vertical" style={{ minHeight: '100vh', background: t.pageBg }}>
      <Content
        style={{
          padding: 0,
          margin: 0,
          maxWidth: '100%',
          width: '100%',
          minHeight: '100vh',
        }}
      >
        <div style={{ overflow: 'hidden', padding: 4 }}>
          {/* Header الطولي: لوغو + اسم المكتب أقصى اليسار | التاريخ والوقت أقصى اليمين + أزرار */}
          <div
            style={{
              direction: "ltr",
              borderRadius: 12,
              border: `1px solid ${t.border}`,
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 14,
              padding: '6px 10px',
              background: t.cardBg,
              boxShadow: isDark ? '0 10px 30px rgba(0,0,0,0.25)' : '0 10px 30px rgba(2, 19, 35, 0.08)',
              marginBottom: 10,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {office?.logo ? (
                <img src={office.logo} alt="Logo" style={{ width: 128, height: 128, objectFit: 'contain', borderRadius: 14, flexShrink: 0 }} />
              ) : (
                <div style={{ width: 128, height: 128, background: isDark ? 'rgba(96, 127, 166, 0.2)' : '#f1f5f9', borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', color: t.textSecondary, fontSize: 14, flexShrink: 0 }}>Logo</div>
              )}
              <span style={{ fontSize: 36, fontWeight: 800, color: t.text, letterSpacing: 0.2 }}>{office?.name || '—'}</span>
            </div>
            <span style={{ marginLeft: 'auto', fontSize: 16, color: t.text, fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
              {formatSyriaDateTime(currentTime).date} {formatSyriaDateTime(currentTime).time}
              {lastUpdate && (
                <span style={{ marginLeft: 10, color: '#22c55e', fontSize: 14, fontWeight: 700 }}>• آخر تحديث: {formatSyriaDateTime(lastUpdate).date} {formatSyriaDateTime(lastUpdate).time}</span>
              )}
            </span>
            <button
              onClick={toggleLayout}
              title={effectiveIsHorizontal ? 'التبديل إلى العرض الطولي' : 'التبديل إلى العرض العرضي'}
              style={{
                background: isDark ? 'rgba(96, 127, 166, 0.2)' : '#f1f5f9',
                border: `1px solid ${t.border}`,
                borderRadius: 10,
                padding: 10,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: t.text,
              }}
            >
              {effectiveIsHorizontal ? <MenuOutlined style={{ fontSize: 18 }} /> : <ColumnWidthOutlined style={{ fontSize: 18 }} />}
              <span style={{ marginInlineStart: 6, fontSize: 13, fontWeight: 700 }}>{effectiveIsHorizontal ? 'طولي' : 'عرضي'}</span>
            </button>
            <button
              onClick={handleToggleTheme}
              style={{
                background: isDark ? 'rgba(96, 127, 166, 0.2)' : '#f1f5f9',
                border: `1px solid ${t.border}`,
                borderRadius: 10,
                padding: 10,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: t.text,
              }}
            >
              {isDark ? <SunOutlined style={{ fontSize: 18 }} /> : <MoonOutlined style={{ fontSize: 18 }} />}
            </button>
          </div>

          {/* Video — النمط الطولي (ارتفاع ثابت كما كان سابقاً) */}
          {hasAnyVideo ? (
            <div style={{
              width: '100%',
              background: isDark ? 'rgba(96, 127, 166, 0.2)' : '#f1f5f9',
              borderRadius: "8px",
              border: `1px solid ${t.border}`,
              marginBottom: 8,
            }}>
              {currentVideoUrl &&
                (currentVideoUrl.includes('youtube.com') || currentVideoUrl.includes('vimeo.com') ? (
                  <iframe
                    src={currentVideoUrl}
                    width="100%"
                    height={420}
                    frameBorder="0"
                    allow="autoplay; encrypted-media"
                    allowFullScreen
                    title="Video"
                    style={{ display: 'block' }}
                  />
                ) : (
                  <video
                    key={currentVideoUrl}
                    src={currentVideoUrl}
                    autoPlay
                    loop={!hasMultipleVideos}
                    muted
                    playsInline
                    controls
                    style={{
                      borderStartStartRadius: "8px",
                      borderStartEndRadius: "8px",
                      border: `1px solid ${t.border}`,
                      width: '100%',
                      height: 420,
                      objectFit: 'cover',
                      display: 'block',
                    }}
                    onEnded={hasMultipleVideos ? () => setActiveVideo(activeVideo === 'main' ? 'option' : 'main') : undefined}
                  />
                ))}
              <div
                style={{
                  borderEndEndRadius: "8px",
                  borderEndStartRadius: "8px",
                  padding: '8px 24px',
                  background: isDark ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.05)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 8,
                }}
              >
                <span style={{ fontSize: 13, color: t.textSecondary }}>Video Name</span>
                {hasMultipleVideos && (
                  <button
                    onClick={() => setActiveVideo(activeVideo === 'main' ? 'option' : 'main')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      background: 'transparent',
                      border: `1px solid ${t.border}`,
                      borderRadius: 8,
                      padding: '6px 12px',
                      cursor: 'pointer',
                      fontSize: 13,
                      color: t.text,
                    }}
                  >
                    <SwapOutlined />
                    {activeVideo === 'main' ? 'الفيديو الثاني' : 'الفيديو الأول'}
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div
              style={{
                padding: 48,
                textAlign: 'center',
                background: isDark ? 'rgba(96, 127, 166, 0.06)' : '#f8fafc',
                borderRadius: 8,
                marginBottom: 8,
                border: `1px solid ${t.border}`,
              }}
            >
              <PlayCircleOutlined style={{ fontSize: 40, color: t.textSecondary, marginBottom: 12 }} />
              <div style={{ color: t.textSecondary, fontSize: 15 }}>لا يوجد فيديو متاح حالياً</div>
            </div>
          )}

          {/* Table — بدون بحث */}
          <div style={{ padding: isHorizontal ? 0 : '0 8px 8px' }}>
            <div style={{ border: `1px solid ${t.border}`, borderRadius: 8, overflow: 'hidden' }}>
              <Table
                style={{ direction: "rtl" }}
                dataSource={currencies}
                columns={columns}
                rowKey="id"
                pagination={false}
                showHeader
                rowClassName={(_, i) => (i % 2 === 1 ? 'display-row-alt' : 'display-row')}
                locale={{ emptyText: 'لا توجد عملات متاحة' }}
              />
            </div>
          </div>

          {/* Footer: Live + Last update | All Rates → SYP */}
          <div
            style={{
              direction: "ltr",
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 12,
              padding: '16px 24px',
              background: t.cardBg,
              borderRadius: 8,
              border: `1px solid ${t.border}`, marginBottom: "8px"
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span
                style={{
                  width: 21,
                  height: 21,
                  borderRadius: '50%',
                  background: '#22c55e',
                  flexShrink: 0,
                }}
              />
              <span style={{ fontWeight: 600, color: '#22c55e', fontSize: "40px" }}>Live</span>
              <span style={{
                fontSize: "24px", color: t.textSecondary,
                fontWeight: 600,

              }}>
                LAST UPDATE AT {lastUpdate ? formatSyriaDateTime(lastUpdate).time : '--'}
              </span>
            </div>
            <span style={{

              fontSize: "24px", color: t.textSecondary,
              fontWeight: 600,
            }}>
              All Rates are foreign currency → SYP
            </span>
          </div>
        </div>

        <style>{`
          .ant-table { background: transparent !important; }
          .ant-table-thead > tr > th {
            background: ${t.tableHeaderBg} !important;
            color: ${t.text} !important;
            font-weight: 700 !important;
            font-size: clamp(18px, 2.4vw, 32px) !important;
            border-bottom: 1px solid ${t.border} !important;
            padding: 16px 20px !important;
            min-height: 64px !important;
            vertical-align: middle !important;
          }
          .ant-table-thead > tr > th:nth-child(1) { color: #e53935 !important; font-weight: 800 !important; }
          .ant-table-thead > tr > th:nth-child(2) { color: #00c853 !important; font-weight: 800 !important; }
          .display-row > td { background: ${t.rowBg} !important; border-bottom: 1px solid ${t.border} !important; padding: 0px !important; }
          .display-row-alt > td { background: ${t.rowAltBg} !important; border-bottom: 1px solid ${t.border} !important; padding: 8px 12px !important; }
          .ant-table-tbody > tr:hover > td { background: ${isDark ? 'rgba(96, 127, 166, 0.1)' : 'rgba(96, 127, 166, 0.06)'} !important; }
          .ant-table-tbody > tr:last-child > td { border-bottom: none !important; }
          @keyframes displayWaveBlinkBuy {
            0%, 100% { transform: translateY(0) scale(1); opacity: 1; filter: brightness(1); }
            50% { transform: translateY(-10px) scale(1.03); opacity: 0.85; filter: brightness(1.15); }
          }
          @keyframes displayWaveBlinkSell {
            0%, 100% { transform: translateY(0) scale(1); opacity: 1; filter: brightness(1); }
            50% { transform: translateY(-10px) scale(1.03); opacity: 0.85; filter: brightness(1.15); }
          }
          .display-rate-cell.display-rate-buy { animation: displayWaveBlinkBuy 2.2s ease-in-out infinite; }
          .display-rate-cell.display-rate-sell { animation: displayWaveBlinkSell 2.2s ease-in-out infinite; }
          @keyframes displayFlagPulse {
            0%, 100% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.18); opacity: 0.92; }
          }
          .display-flag-icon { display: inline-block; }
          .ant-input { color: ${t.text} !important; }
          .ant-input::placeholder { color: ${t.textSecondary} !important; }
          .ant-empty-description { color: ${t.textSecondary} !important; }
        `}</style>
      </Content>
    </Layout >
  );
};

export default Display;
